import rg4js from "raygun4js";
import { lazy, useEffect, useState } from "react";
import { Redirect, Route, Switch, useHistory } from "react-router-dom";
import GeneralInformationScreen from "@/components/ConfirmInfo/GeneralInformation";
import UpdateFormInterrogatories from "@/components/DocumentWizard/UpdateFormInterrogatories";
import UpdateInterrogatories from "@/components/DocumentWizard/UpdateInterrogatories";
import Loading from "@/components/Loading";
import Tutorials from "@/components/Tutorials";
import { useAuth } from "@/hooks/useAuth";
import useMixpanel from "@/hooks/useMixpanel";
import ClientsMulti from "@/screens/ClientManagementMulti";
import Error404 from "@/screens/Error/Error404";
import FirmScreen from "@/screens/Firm";
import AddEditObjection from "@/screens/Library/AddEditObjection";
import Library from "@/screens/Library/Library";
import Quarantine from "@/screens/Quarantine";
import "./css/App.scss";
import { UserType } from "@/briefpoint-client";
import { PrivateRoute } from "@/Route";
import ArchiveListScreen from "@/screens/Archive/ArchiveListScreen";
import Cases from "@/screens/CaseManagementMulti";
import CaseOverviewPage from "@/screens/CaseManagementMulti/case";
import ClientPageMulti from "@/screens/ClientManagementMulti/client";
import GenerateDiscoveryRequestPage from "@/screens/DocumentGeneration/GenerateDiscoveryRequestPage";
import DocumentList from "@/screens/DocumentList";
import JurisdictionSelect from "@/screens/JurisdictionSelect";
import AddEditQuestion from "@/screens/Library/AddEditQuestion";
import Logout from "@/screens/Logout";
import OnBehalfOfSelect from "@/screens/OnBehalfOfSelect";
import SupplementalResponse from "@/screens/SupplementalResponse";
import TermsAndConditions from "@/screens/TermsAndConditions";
import UserAuth from "@/UserAuth";

const AdminScreen = lazy(() => import("@/screens/Admin"));
const AdminDocumentsScreen = lazy(
  () => import("@/screens/Admin/adminDocumentList"),
);
const AdminRevenueScreen = lazy(
  () => import("@/screens/Admin/adminRevenueScreen"),
);
const AdminFirmScreen = lazy(() => import("@/screens/Admin/adminFirmScreen"));
const AdminFirmDetails = lazy(() => import("@/screens/Admin/adminFirmDetails"));
const AdminFirmCasesScreen = lazy(
  () => import("@/screens/Admin/adminFirmCasesScreen"),
);
const AdminCaseScreen = lazy(() => import("@/screens/Admin/adminCaseScreen"));
const AdminClientScreen = lazy(
  () => import("@/screens/Admin/adminClientScreen"),
);
const AdminCaseProductionScreen = lazy(
  () => import("@/screens/Admin/adminCaseProductionScreen"),
);
const AdminCaseFileDetailsScreen = lazy(
  () => import("@/screens/Admin/adminCaseFileDetailsScreen"),
);
const AdminMiscScreen = lazy(() => import("@/screens/Admin/adminMisc"));
const UserDocument = lazy(() => import("@/components/Admin/UserDocument"));
const UserDocuments = lazy(() => import("@/components/Admin/UserDocuments"));
const DocumentWizard = lazy(() => import("@/components/DocumentWizard"));
const DocumentWizardSourceDocumentReview = lazy(
  () => import("@/components/DocumentWizard/SourceDocumentReview"),
);
const PropoundDocumentReview = lazy(
  () => import("@/components/DocumentWizard/PropoundDocumentReview/index"),
);
const ClientFeedback = lazy(() => import("@/screens/ClientFeedback"));
const ClientInfo = lazy(() => import("@/screens/ClientInfo"));

export function LoadingComponent() {
  return <Loading className="mainApp mt-3" isLoading />;
}

function App() {
  return (
    <UserAuth>
      <AppAuthenticated />
    </UserAuth>
  );
}

function AppAuthenticated() {
  const { user, firm, getUser, createUser, baseUser, firmAddOffice } =
    useAuth();
  const [showTerms, setShowTerms] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [mixpanelTrack] = useMixpanel();
  const history = useHistory();

  useEffect(() => {
    const effect = () => {
      rg4js("trackEvent", {
        type: "pageView",
        path: history.location.pathname,
      });
    };
    return effect;
  }, [history]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: depending on getUser causes the terms page to briefly show on logout
  useEffect(() => {
    const getUserInner = async () => {
      try {
        await getUser();
      } catch (error) {
        if (error instanceof Response && error.status === 401) {
          createUserInner();
          //setShowTerms(true); There is currently a bug in mobile safari that prevents users from agreeing to T&C
        }
      }
    };
    getUserInner();
  }, []);

  const createUserInner = async () => {
    setCreatingUser(true);
    await createUser(false);
    mixpanelTrack("Signup");
    setShowTerms(false);
  };

  if (showTerms) {
    return (
      <TermsAndConditions isLoading={creatingUser} onAgree={createUserInner} />
    );
  }

  if (!user || !firm || !baseUser) {
    return <LoadingComponent />;
  }

  if (baseUser.isQuarantined || user.isQuarantined) {
    return <Quarantine />;
  }
  const showParalegalSelect =
    baseUser.type === UserType.Paralegal && user.id === baseUser.id;

  if (showParalegalSelect) {
    return <OnBehalfOfSelect />;
  }

  if (!firm?.offices?.length) {
    return <JurisdictionSelect firm={firm} addOffice={firmAddOffice} />;
  }
  const DefaultScreen = DocumentList;

  return (
    <Switch>
      <Route exact path="/">
        <DefaultScreen />
      </Route>
      <PrivateRoute path="/logout">
        <Logout />
      </PrivateRoute>
      <PrivateRoute path="/documents/archive">
        <ArchiveListScreen />
      </PrivateRoute>
      <PrivateRoute path="/documents/:documentId/client-feedback/:communicationId/confirm-client-info">
        <ClientInfo />
      </PrivateRoute>
      <PrivateRoute path="/documents/:documentId/client-feedback">
        <ClientFeedback />
      </PrivateRoute>
      <PrivateRoute path="/documents/:documentId/supplemental-response">
        <SupplementalResponse />
      </PrivateRoute>
      <PrivateRoute path="/documents">
        <DocumentList />
      </PrivateRoute>
      <PrivateRoute path="/general-info">
        <GeneralInformationScreen />
      </PrivateRoute>
      <PrivateRoute path="/wizard/:documentId/source-document-review">
        <DocumentWizardSourceDocumentReview />
      </PrivateRoute>
      <PrivateRoute path="/wizard/propound/:documentId">
        <PropoundDocumentReview />
      </PrivateRoute>
      <PrivateRoute path="/wizard/:documentId/update-interrogatories/:interrogatoryId">
        <UpdateInterrogatories />
      </PrivateRoute>
      <PrivateRoute path="/wizard/:documentId/update-form-interrogatories">
        <UpdateFormInterrogatories />
      </PrivateRoute>
      <PrivateRoute path="/wizard/:documentId/:currentStep?/:caseId?/:firstFolderId?/:secondFolderId?/:thirdFolderId?">
        <DocumentWizard />
      </PrivateRoute>
      <PrivateRoute path="/library/addedit">
        <AddEditObjection />
      </PrivateRoute>
      <PrivateRoute path="/library/addeditquestion">
        <AddEditQuestion />
      </PrivateRoute>
      <PrivateRoute path="/library">
        <Library />
      </PrivateRoute>
      <PrivateRoute path="/clients/:clientId/cases/:id/generateDiscoveryRequest">
        <GenerateDiscoveryRequestPage />
      </PrivateRoute>
      <PrivateRoute path="/clients/:id">
        <ClientPageMulti />
      </PrivateRoute>{" "}
      :
      <PrivateRoute path="/clients">
        <ClientsMulti />
      </PrivateRoute>
      <PrivateRoute path="/cases/:id/:firstFolderId?/:secondFolderId?/:thirdFolderId?">
        <CaseOverviewPage />
      </PrivateRoute>
      <PrivateRoute path="/cases">
        <Cases />
      </PrivateRoute>
      <PrivateRoute path="/tutorials">
        <Tutorials />
      </PrivateRoute>
      <PrivateRoute exact path="/how-to">
        <Redirect to="/tutorials" />
      </PrivateRoute>
      <PrivateRoute path="/admin/users/:userId/documents/:documentId">
        <UserDocument />
      </PrivateRoute>
      <PrivateRoute path="/admin/users/:userId/documents">
        <UserDocuments />
      </PrivateRoute>
      <PrivateRoute path="/admin/documents">
        <AdminDocumentsScreen />
      </PrivateRoute>
      <PrivateRoute path="/admin/revenue">
        <AdminRevenueScreen />
      </PrivateRoute>
      <PrivateRoute path="/admin/firm/:firmId/cases/:caseId/production">
        <AdminCaseProductionScreen />
      </PrivateRoute>
      <PrivateRoute path="/admin/firm/:firmId/cases">
        <AdminFirmCasesScreen />
      </PrivateRoute>
      <PrivateRoute path="/admin/case/:caseId">
        <AdminCaseScreen />
      </PrivateRoute>
      <PrivateRoute path="/admin/client/:clientId">
        <AdminClientScreen />
      </PrivateRoute>
      <PrivateRoute path="/admin/case-file/:caseFileId">
        <AdminCaseFileDetailsScreen />
      </PrivateRoute>
      <PrivateRoute path="/admin/firm/:firmId">
        <AdminFirmDetails />
      </PrivateRoute>
      <PrivateRoute path="/admin/firm">
        <AdminFirmScreen />
      </PrivateRoute>
      <PrivateRoute path="/admin/misc">
        <AdminMiscScreen />
      </PrivateRoute>
      <PrivateRoute path="/admin">
        <AdminScreen />
      </PrivateRoute>
      <PrivateRoute path="/firm">
        <FirmScreen />
      </PrivateRoute>
      <Route>
        <Error404 />
      </Route>
    </Switch>
  );
}

export default App;
